/* eslint-disable no-unused-vars */
/**
 * Interfaces typées du parcours d'inscription AICI client (src/App/AiciOnboarding).
 * Une interface "Data" + une interface "Errors" par étape de formulaire.
 * Les données durables sont persistées côté serveur (TeachrContact) et un instantané
 * léger (étape courante) est conservé dans Redux (slice `aiciOnboarding`).
 */

/** Option {name, code} issue de lettreVoieList / typeVoie (src/Utils/Function.tsx). */
export interface VoieOption {
  name: string;
  code: string;
}

/** Étape 1 Informations personnelles (partie 1). */
export interface PersonalInfoData {
  /** Sémantique URSSAF : '1' = homme (MAN), '2' = femme (WOMAN). '' = non renseigné. */
  gender: string;
  /** Prénom(s) du titulaire. */
  firstName: string;
  /** Nom d'usage. */
  name: string;
  /** Nom de naissance (si différent du nom d'usage) optionnel. */
  birthName: string;
  mail: string;
  phoneNumber: string;
  /** Nom de l'élève (si différent du titulaire) optionnel. */
  studentName: string;
}
export interface PersonalInfoErrors {
  gender?: string;
  firstName?: string;
  name?: string;
  birthName?: string;
  mail?: string;
  phoneNumber?: string;
  studentName?: string;
}

/** Étape 2 Naissance + coordonnées bancaires. */
export interface BirthBankData {
  /** Date de naissance au format 'DD-MM-YYYY'. */
  birthDay: string;
  /** Pays de naissance (label renvoyé par SelectCountry). */
  country: string;
  /** Code postal de naissance requis seulement si pays = France. */
  birthZipCode: string;
  /** Ville de naissance requise seulement si pays = France. */
  birthCity: string;
  iban: string;
  bic: string;
  /** Titulaire du compte bancaire (pré-rempli = prénom + nom). */
  accountHolder: string;
}
export interface BirthBankErrors {
  birthDay?: string;
  country?: string;
  birthZipCode?: string;
  birthCity?: string;
  iban?: string;
  bic?: string;
  accountHolder?: string;
}

/** Étape 3 Adresse du domicile fiscal (saisie manuelle). */
export interface AddressData {
  /** Numéro de voie : 4 caractères numériques max, obligatoire. */
  streetNumber: string;
  /** Lettre de voie (Bis, Ter…) optionnelle. */
  streetLetter: VoieOption | null;
  /** Type de voie (Rue, Avenue…) obligatoire. */
  streetType: VoieOption | null;
  /** Libellé de voie : 28 caractères alphanumériques max, obligatoire. */
  streetLabel: string;
  /** Complément d'adresse : 38 caractères alphanumériques max optionnel. */
  complement: string;
  /** Complément lieu-dit : 38 caractères max, validé par pattern optionnel. */
  lieuDit: string;
  zipCode: string;
  city: string;
}
export interface AddressErrors {
  streetNumber?: string;
  streetType?: string;
  streetLabel?: string;
  complement?: string;
  lieuDit?: string;
  zipCode?: string;
  city?: string;
}

/** Étape 4 Acceptation des conditions. */
export interface ConditionsData {
  /** Politique de confidentialité / données. */
  terms: boolean;
  /** Autorisation de transmission URSSAF. */
  cgu: boolean;
  /** CGU / CGV. */
  cgv: boolean;
  /** Souhaite être notifié à chaque déclaration. */
  isNotify: boolean;
  /** Accepte les offres promotionnelles. */
  acceptMarketingEmail: boolean;
}
export interface ConditionsErrors {
  terms?: string;
  cgu?: string;
  cgv?: string;
}

/** Numéros d'étapes du stepper (1-indexés). */
export enum AiciStep {
  PersonalInfo = 1,
  BirthBank = 2,
  Address = 3,
  Recap = 4,
  Confirmation = 5,
}
